'use client'

import React, { useMemo } from 'react'
import { $DCprimaryColor, DcChart, colors } from 'pixel-base'

import {
  colorSquare,
  baseChartStyles,
  createYAxisConfig,
  applySeriesStyles,
} from './chartStyles'
import { useDashboardData, DashboardFilters } from './useDashboardData'

interface OrdersChartProps {
  filters: DashboardFilters
  height?: number | string
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

interface ChartSeries {
  name: string
  type: string
  stack: string
  yAxisIndex: number
  showSymbol: boolean
  symbol: string
  symbolSize: number
  data: number[]
  itemStyle: { color: string }
  z: number
  emphasis?: {
    focus: string
    itemStyle: {
      color: string
    }
  }
}

interface ChartData {
  xAxis: string[]
  dualYAxis: boolean
  showSymbol: boolean
  yAxisNames: string[]
  series: ChartSeries[]
}

interface ChartParam {
  axisValue: string
  value: number
  color: string
  seriesName: string
}

const OrdersChart: React.FC<OrdersChartProps> = ({
  filters,
  setComponent,
  setShowDetail,
  height = 350,
}) => {
  const { data, loading, xAxisDates, totalOrders } = useDashboardData(
    filters,
    'orders',
  )

  // Calculate total net orders and ratio
  const totalNetOrders = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, item) => sum + (item.net_orders || 0), 0)
  }, [data])

  // Calculate net to gross ratio
  const netToGrossRatio = useMemo(() => {
    if (totalOrders === 0) return 0
    return ((totalNetOrders / totalOrders) * 100).toFixed(1)
  }, [totalOrders, totalNetOrders])

  const formattedData = useMemo((): ChartData => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        xAxis: [],
        dualYAxis: false,
        showSymbol: false,
        yAxisNames: ['Orders'],
        series: [],
      }
    }

    // Extract values for series - orders and net orders data
    const ordersData = data.map((item) => item.total_orders || 0)
    const netOrdersData = data.map((item) => item.net_orders || 0)

    return {
      xAxis: xAxisDates,
      dualYAxis: false, // Use single Y-axis scale
      showSymbol: false,
      yAxisNames: ['Orders'],
      series: [
        {
          name: 'Gross Orders',
          type: 'line',
          stack: 'orders',
          yAxisIndex: 0,
          showSymbol: false,
          symbol: 'none',
          symbolSize: 0,
          data: ordersData,
          itemStyle: { color: $DCprimaryColor },
          z: 1,
        },
        {
          name: 'Net Orders',
          type: 'line',
          stack: 'netOrders',
          yAxisIndex: 0, // Same Y-axis as gross orders
          showSymbol: false,
          symbol: 'none',
          symbolSize: 0,
          data: netOrdersData,
          itemStyle: { color: '#f2bc16' }, // Warning color for net orders
          z: 2,
        },
      ],
    }
  }, [data, xAxisDates])

  const chartOptions = {
    ...baseChartStyles,
    yAxis: createYAxisConfig({ name: 'Orders', color: $DCprimaryColor }),
    tooltip: {
      ...baseChartStyles.tooltip,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#5f38f9',
          width: 2,
          type: 'dashed',
        },
      },
      formatter: (params: ChartParam[]) => {
        if (!params || params.length === 0) return ''

        const date = params[0].axisValue
        let html = `
          <div style="padding: 12px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1); border: 1px solid rgba(0,0,0,0.05);">
            <div style="text-align: center; margin-bottom: 12px;">
              <div style="font-size: 16px; font-weight: 700; color: #2c3e50; margin-bottom: 4px;">${date}</div>
              <div style="height: 2px; background: linear-gradient(90deg, #5f38f9 0%, #7c3aed 100%); margin: 0 auto; width: 60px; border-radius: 1px;"></div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 8px;">
        `

        params.forEach((param: ChartParam) => {
          const value = param.value || 0
          const color = param.color
          const seriesName = param.seriesName

          html += `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255,255,255,0.8); border-radius: 6px; border: 1px solid rgba(0,0,0,0.05);">
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="width: 12px; height: 12px; background: ${color}; border-radius: 3px;"></div>
                <span style="font-size: 12px; font-weight: 600; color: #495057;">${seriesName}</span>
              </div>
              <div style="font-size: 14px; font-weight: 700; color: #2c3e50;">${value}</div>
            </div>
          `
        })

        html += `
            </div>
          </div>
        `

        return html
      },
    },
  }

  return (
    <>
      <DcChart
        type="mixedLineBar"
        title="Orders"
        isLoading={loading}
        headerData={{
          title: 'Orders Summary',
          data: `Gross: ${totalOrders} | Net: ${totalNetOrders} | Ratio: ${netToGrossRatio}%`,
        }}
        isTitleClickable={true}
        onClickTitle={() => {
          setComponent('ordersChart')
          setShowDetail(true)
        }}
        data={formattedData}
        height={height}
        options={{
          ...chartOptions,
          series: applySeriesStyles(formattedData.series, baseChartStyles, [
            $DCprimaryColor,
          ]),
          // Ensure smooth properties are explicitly set
          smooth: true,
          smoothMonotone: 'x',
        }}
      />
    </>
  )
}

export default OrdersChart
